/**
 * NKBA landing-zone requirements per appliance type.
 *
 * "Landing" is a contiguous run of usable countertop immediately
 * adjacent to an appliance — somewhere to set down a hot pan, a
 * stack of plates, groceries from the fridge, etc. NKBA defines
 * minimums; designers always check these even though the rules
 * sometimes bend.
 *
 * `sides` is an array of constraint slots:
 *   - `{ which: 'larger', minWidth }`  — the wider of the two sides
 *     must be ≥ minWidth. Used by range/sink where designer chooses
 *     which side is which.
 *   - `{ which: 'smaller', minWidth }` — the narrower side must be
 *     ≥ minWidth (always paired with a 'larger' entry).
 *   - `{ which: 'handle', minWidth, hardRequireNonZero }` — for
 *     fridge: at least one side must be ≥ minWidth (the "handle
 *     side" — we don't track hinge direction so we accept the
 *     better of left/right). `hardRequireNonZero` means a layout
 *     with ZERO landing on both sides is rejected outright (you
 *     literally can't open the fridge).
 */
const { VANITY_PROFILE_ID } = require('../cabinet-runs/profiles');

const INCH = 0.0254;

const LANDING_REQUIREMENTS = {
  fridge: {
    sides: [{ which: 'handle', minWidth: 15 * INCH, hardRequireNonZero: true }],
  },
  refrigerator: {
    sides: [{ which: 'handle', minWidth: 15 * INCH, hardRequireNonZero: true }],
  },
  range: {
    sides: [
      { which: 'larger', minWidth: 24 * INCH },
      { which: 'smaller', minWidth: 12 * INCH },
    ],
  },
  cooktop: {
    sides: [
      { which: 'larger', minWidth: 24 * INCH },
      { which: 'smaller', minWidth: 12 * INCH },
    ],
  },
  stove: {
    sides: [
      { which: 'larger', minWidth: 24 * INCH },
      { which: 'smaller', minWidth: 12 * INCH },
    ],
  },
  sink: {
    sides: [
      { which: 'larger', minWidth: 18 * INCH },
      { which: 'smaller', minWidth: 18 * INCH },
    ],
  },
  // Wall oven: NKBA wants a ≥15" landing adjacent to the oven (beside, or
  // above/below a built-in — we don't track vertical, so we accept the better
  // of the two horizontal sides, like the fridge's handle side).
  walloven: {
    sides: [{ which: 'handle', minWidth: 15 * INCH }],
  },
};

/**
 * Reach extension: an island or peninsula countertop within this
 * straight-line distance of the appliance's front face counts toward
 * the side it's closest to.
 */
const REACH_EXTENSION_DISTANCE = 48 * INCH;

/**
 * The landing requirement for an appliance, or null when there is none.
 *
 * `profileId` is the RUN's dimension profile (`cabinet-runs/profiles.js`), and
 * a vanity has no landing standard at all: every rule in this table is an
 * NKBA KITCHEN rule about somewhere to set down a hot pan or a stack of
 * plates, and none of it describes a bathroom. The sink entry is the one that
 * matters, and applying it to a vanity is not merely irrelevant but WRONG —
 * 18" of clear counter either side of a basin describes a kitchen sink, while
 * a double vanity routinely has 8", which is normal and not worth a word.
 *
 * Measured on the first real converted vanity (`F6B82D01` bathroom A, a 70.3"
 * two-basin run): both basins raised "Sink landing 8" — NKBA suggests 18"",
 * and those surface as verdict TOASTS, so every vanity bay edit reported two
 * problems with a correct layout.
 *
 * Unknown / absent profile keeps the kitchen answer, matching `profileOf`.
 */
function landingRequirementFor(applianceType, profileId) {
  if (!applianceType) return null;
  if (profileId === VANITY_PROFILE_ID) return null;
  return LANDING_REQUIREMENTS[applianceType.toLowerCase()] || null;
}

module.exports = {
  LANDING_REQUIREMENTS,
  REACH_EXTENSION_DISTANCE,
  landingRequirementFor,
};
